import { useSortable } from "@dnd-kit/sortable"
import { CSS } from "@dnd-kit/utilities"
import styled from "@emotion/styled"
import { PButton } from "@porsche-design-system/components-react"
import {
	colorContrastLow,
	colorPrimary,
	radiusMd,
	spacingStaticXs,
} from "@porsche-design-system/components-react/emotion"
import { shouldForwardProp } from "src/common/shouldForwardProp"

const progressBarHeight = "6px"

interface NewsImageTileProps {
	alt: string
	deleteLabel: string
	/** the tile is shown without the possibility to delete or move it */
	isDisabled: boolean
	isUploading: boolean
	onDelete: () => void
	previewUrl: string | undefined
	/** the share of the image that has been uploaded in whole percents */
	progress: number | undefined
	sortableId: string
	uploadingLabel: string
}

export const NewsImageTile = ({
	alt,
	deleteLabel,
	isDisabled,
	isUploading,
	onDelete,
	previewUrl,
	progress,
	sortableId,
	uploadingLabel,
}: NewsImageTileProps) => {
	const isFixed = isDisabled || isUploading
	const { attributes, isDragging, listeners, setNodeRef, transform, transition } = useSortable({
		disabled: isFixed,
		id: sortableId,
		transition: { duration: 300, easing: "ease" },
	})

	return (
		<Container
			ref={setNodeRef}
			data-testid="news-image-tile"
			$isDragging={isDragging}
			// eslint-disable-next-line no-restricted-syntax
			style={{ transform: CSS.Transform.toString(transform), transition }}
		>
			<Picture $isDraggable={!isFixed} {...attributes} {...listeners}>
				{previewUrl && <Image alt={alt} draggable={false} src={previewUrl} $isUploading={isUploading} />}
				{isUploading && (
					<ProgressBar>
						<progress aria-label={uploadingLabel} max={100} value={progress ?? 0} />
					</ProgressBar>
				)}
			</Picture>
			{!isFixed && (
				<DeleteButton compact hideLabel icon="delete" type="button" onClick={onDelete}>
					{deleteLabel}
				</DeleteButton>
			)}
		</Container>
	)
}

const Container = styled("div", { shouldForwardProp })<{ $isDragging: boolean }>`
	position: relative;
	z-index: ${({ $isDragging }) => ($isDragging ? 1 : 0)};
`

const Picture = styled("div", { shouldForwardProp })<{ $isDraggable: boolean }>`
	position: relative;
	aspect-ratio: 4 / 3;
	overflow: hidden;
	border-radius: ${radiusMd};
	cursor: ${({ $isDraggable }) => ($isDraggable ? "grab" : "default")};
	touch-action: ${({ $isDraggable }) => ($isDraggable ? "none" : "auto")};
`

const Image = styled("img", { shouldForwardProp })<{ $isUploading: boolean }>`
	display: block;
	width: 100%;
	height: 100%;
	object-fit: cover;
	opacity: ${({ $isUploading }) => ($isUploading ? 0.4 : 1)};
`

const ProgressBar = styled.div`
	position: absolute;
	inset-inline: 0;
	inset-block-end: 0;

	> progress {
		display: block;
		width: 100%;
		height: ${progressBarHeight};
		border: none;
		appearance: none;
		background: transparent;
		color: ${colorPrimary};

		/* the track has to be painted once, because the color is translucent and would stack up */
		&::-webkit-progress-bar {
			background: ${colorContrastLow};
		}

		&::-webkit-progress-value {
			background: ${colorPrimary};
			transition: width 0.2s ease;
		}

		@supports (-moz-appearance: none) {
			background: ${colorContrastLow};
		}

		&::-moz-progress-bar {
			background: ${colorPrimary};
		}

		@media (forced-colors: active) {
			appearance: auto;
		}
	}
`

const DeleteButton = styled(PButton)`
	position: absolute;
	inset-block-start: ${spacingStaticXs};
	inset-inline-end: ${spacingStaticXs};
`
